import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function DeleteAccount() {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/account/request-deletion`, { email, reason });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to submit request. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e27]" data-testid="delete-account-page">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-10 w-10 object-contain" />
            <span className="text-white font-extrabold text-lg">Sports Predictions & Tips</span>
          </div>
          <Link to="/login" className="text-emerald-400 hover:text-emerald-300 text-sm font-bold transition-colors">
            Back to App
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-extrabold text-white mb-2">Account Deletion</h1>
        <p className="text-white/40 text-sm mb-10">
          Sports Predictions & Tips — Request to delete your account and associated data
        </p>

        {/* How to Request Deletion */}
        <section className="mb-10">
          <h2 className="text-lg font-extrabold text-white mb-4 flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-extrabold">1</span>
            How to Request Account Deletion
          </h2>
          <div className="bg-white/5 rounded-xl border border-white/10 p-5 text-white/60 text-sm leading-relaxed space-y-3">
            <p>You can request the deletion of your <strong className="text-white">Sports Predictions & Tips</strong> account in one of the following ways:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><strong className="text-white">Use the form below</strong> — Submit your registered email address and we will process your deletion request.</li>
              <li><strong className="text-white">Email us directly</strong> — Send a deletion request to <a href="mailto:support@sportspredictionstips.com" className="text-emerald-400 hover:underline">support@sportspredictionstips.com</a> from the email associated with your account.</li>
              <li><strong className="text-white">From the app</strong> — Go to Profile → Settings → Contact Support and request account deletion.</li>
            </ul>
            <p>All deletion requests are processed within <strong className="text-white">7 business days</strong>. You will receive an email confirmation when your account has been deleted.</p>
          </div>
        </section>

        {/* What Data Will Be Deleted */}
        <section className="mb-10">
          <h2 className="text-lg font-extrabold text-white mb-4 flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-red-500/20 flex items-center justify-center text-red-400 text-sm font-extrabold">2</span>
            What Data Will Be Deleted
          </h2>
          <div className="bg-white/5 rounded-xl border border-white/10 p-5 text-white/60 text-sm leading-relaxed space-y-3">
            <p>When your account is deleted, the following data will be <strong className="text-white">permanently removed</strong> from our systems:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Your account credentials (email, password hash)</li>
              <li>Profile information (name, phone number, birthday, city, profile photo)</li>
              <li>Prediction viewing history and activity logs</li>
              <li>Push notification tokens and device identifiers</li>
              <li>Session data and authentication tokens</li>
            </ul>
          </div>
        </section>

        {/* What Data May Be Retained */}
        <section className="mb-10">
          <h2 className="text-lg font-extrabold text-white mb-4 flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-extrabold">3</span>
            What Data May Be Retained
          </h2>
          <div className="bg-white/5 rounded-xl border border-white/10 p-5 text-white/60 text-sm leading-relaxed space-y-3">
            <p>Certain data may be retained for a limited period after account deletion for legal and business purposes:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li><strong className="text-white">Payment/subscription records</strong> — Transaction history with Stripe or Google Play is retained for <strong className="text-white">up to 90 days</strong> after deletion to comply with financial reporting obligations and handle any refund disputes.</li>
              <li><strong className="text-white">Anonymized analytics data</strong> — Aggregated, non-identifiable usage statistics (e.g., total page views) may be retained indefinitely as they cannot be linked back to you.</li>
              <li><strong className="text-white">Legal compliance data</strong> — If required by law, certain records may be retained for the legally mandated period.</li>
            </ul>
            <p className="mt-2">After the retention period, all remaining data is permanently deleted.</p>
          </div>
        </section>

        {/* Deletion Request Form */}
        <section className="mb-10">
          <h2 className="text-lg font-extrabold text-white mb-4 flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-400 text-sm font-extrabold">4</span>
            Submit Deletion Request
          </h2>

          {submitted ? (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 text-center" data-testid="deletion-success">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <svg className="w-7 h-7 text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
              </div>
              <h3 className="text-xl font-extrabold text-white mb-2">Request Submitted</h3>
              <p className="text-white/50 text-sm">
                Your account deletion request has been received. We will process it within 7 business days and send a confirmation to your email.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white/5 rounded-xl border border-white/10 p-6 space-y-4" data-testid="deletion-form">
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm font-bold" data-testid="deletion-error">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-[10px] font-bold text-white/50 mb-1.5 uppercase tracking-wider">Email Address</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                  placeholder="Enter the email associated with your account"
                  data-testid="deletion-email-input"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-white/50 mb-1.5 uppercase tracking-wider">Reason (Optional)</label>
                <textarea
                  value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us why you want to delete your account (optional)"
                  data-testid="deletion-reason-input"
                />
              </div>
              <button
                type="submit" disabled={loading}
                className="w-full py-3.5 bg-red-500/80 hover:bg-red-500 text-white font-extrabold rounded-xl transition-all disabled:opacity-50 text-sm"
                data-testid="deletion-submit-button"
              >
                {loading ? 'Submitting...' : 'Request Account Deletion'}
              </button>
              <p className="text-white/30 text-xs text-center">
                By submitting, you confirm that you are the owner of this account.
              </p>
            </form>
          )}
        </section>

        {/* Contact */}
        <div className="text-center border-t border-white/10 pt-8">
          <p className="text-white/30 text-xs">
            Questions? Contact us at{' '}
            <a href="mailto:support@sportspredictionstips.com" className="text-emerald-400 hover:underline">support@sportspredictionstips.com</a>
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="text-white/30 hover:text-white/50 text-xs font-bold transition-colors">Terms & Privacy</Link>
            <Link to="/login" className="text-white/30 hover:text-white/50 text-xs font-bold transition-colors">Back to App</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeleteAccount;
